// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// `/headset` — how to get from the phone in your hand to the Quest on the shelf (M5-31, 08 §7).
//
// A Pro subscriber on a phone or a desktop taps **Enter VR** and lands here, because the browser
// they are holding cannot present immersive WebXR. The page's whole job is to move them to a
// browser that can, without making them type a URL into a headset — which is the single most
// unpleasant thing a Quest asks anybody to do.
//
// **The QR payload carries the scheme, and that is not a detail.** 08 §7 records both halves of
// why, and both were learned on device: a bare `host:port` payload is not recognised as a link by
// phone cameras, and HTTPS-Only Safari refuses an `http://` one. `qrPayloadProblem` refuses a
// payload without a scheme rather than rendering a code that scans perfectly and does nothing.
//
// **The URL is the one they are already on.** Not a marketing address, not a short link — the
// current origin plus the path they came from, so a player who was looking at NEON NOVA arrives
// at NEON NOVA. Copied verbatim into the code and shown underneath it in full, because a QR code
// is unreadable by a human and a player who cannot scan it has to be able to type it.

import { useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { encodeQr, qrPath, qrPayloadProblem, qrViewBox } from '../../ui/qr'
import { useLocalisation } from '../i18n/useLanguage'

/**
 * Where the player should end up on the headset.
 *
 * `returnTo` if it is a same-origin path, else the front door. Same-origin is checked rather than
 * assumed: this value ends up in a QR code that a stranger's phone will open, and a `returnTo`
 * that could carry an absolute URL would make the page an open redirector with a camera-friendly
 * delivery mechanism.
 */
export function handoffUrl(origin: string, search: string, fallback = '/'): string {
  const wanted = new URLSearchParams(search).get('returnTo')
  const path = wanted !== null && wanted.startsWith('/') && !wanted.startsWith('//') ? wanted : fallback
  return `${origin}${path}`
}

export function Headset(): React.JSX.Element {
  const { t } = useLocalisation()
  const location = useLocation()
  const origin = typeof globalThis.location === 'undefined' ? '' : globalThis.location.origin
  const url = handoffUrl(origin, location.search)

  // Encoding is real work — Reed–Solomon over ten candidate masks — and the URL changes only when
  // the address does, so it is memoised against the address rather than run on every render.
  const code = useMemo(() => {
    const problem = qrPayloadProblem(url)
    if (problem !== undefined) return { problem }
    const symbol = encodeQr(url)
    return { symbol, d: qrPath(symbol), viewBox: qrViewBox(symbol) }
  }, [url])

  return (
    <div className="page headset">
      {/* S3-04, as on every other page: the heading is the page’s, not the navigation’s. */}
      <h1>{t('route.title.headset')}</h1>
      <p className="page__lede">{t('headset.lede')}</p>

      {'symbol' in code && code.symbol !== undefined ? (
        <figure className="headset__code">
          {/*
            `img` role with a label, because to a screen reader a QR code is a picture of a URL
            and the URL is the useful part. The path is one element rather than one per module:
            a version-10 symbol is 3,249 of them.
          */}
          <svg
            className="headset__qr"
            viewBox={code.viewBox}
            role="img"
            aria-label={t('headset.qr-title', { url })}
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="100%" height="100%" fill="#ffffff" />
            <path d={code.d} fill="#000000" />
          </svg>
          <figcaption>{t('headset.scan')}</figcaption>
        </figure>
      ) : (
        <p className="page__notice" role="status">
          {t('headset.no-code')}
          {'problem' in code && code.problem !== undefined ? ` — ${code.problem}` : ''}
          {t('headset.no-code-tail')}
        </p>
      )}

      <p className="headset__url">
        <code>{url}</code>
      </p>

      <ol className="headset__steps">
        <li>{t('headset.step-1')}</li>
        <li>{t('headset.step-2')}</li>
        <li>{t('headset.step-3')}</li>
        <li>
          {t('headset.press')} <strong>{t('xr.enter-vr')}</strong> {t('headset.or')}{' '}
          <strong>{t('xr.enter-mr')}</strong> {t('headset.on-machine')}
        </li>
      </ol>

      <p className="page__aside">
        {t('headset.no-headset')} <Link to="/">{t('headset.windowed')}</Link>{' '}
        {t('headset.windowed-tail')}
      </p>
    </div>
  )
}
